import { Injectable, Inject } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import * as Rollbar from 'rollbar';

// Services
import { ErrorDialogService } from '@app/services/root';
import { RollbarService } from '../rollbar';

// Resources
import * as resources from '@app/resources';
import * as constants from '@app/constants';
import { Statuses } from '@app/shared/enums';
import { GoogleAnalyticsService } from '../services/google-analytics.service';

@Injectable({
    providedIn: 'root'
})
export class ErrorInterceptor implements HttpInterceptor {
    private blacklist = [
        ...resources.geoUrls,
        ...resources.baseDataUrls,
        ...resources.registrationProcessingMetadataUrls,
        ...resources.loginBridge,
        ...resources.completeTxn
    ];
    private ignoreByUrlAndErrorCodeList = [...resources.ignoreByUrlAndErrorCode];
    constructor(
        private errorDialogService: ErrorDialogService,
        @Inject(RollbarService) private rollbar: Rollbar,
        private gaService: GoogleAnalyticsService,
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if (!this.findUrl(req) && !this.findUrlAndErrorCode(req, error)) {
                    this.validateError(req, error);
                }
                // Only place where rollbar tracks errors
                if (this.reportRollbarError(error.error)) {
                    const request = error.error;
                    let messageToLog;
                    if (request && request.hasOwnProperty('errorCode') && request.hasOwnProperty('errorMessage')) {
                        messageToLog = `${req.url} ${request.errorMessage}`;
                    } else {
                        messageToLog = `${req.url} (StatusCode ${error.status}) ${error.message}`;
                    }
                    this.reportErrorGA(messageToLog);
                    this.rollbar.error(messageToLog);
                }

                // TODO: Handle the exception
                return throwError(error);
            }),
            tap((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.status === Statuses.FAILED && req.url.includes(resources.preflightCommand) && req.method === 'POST') {
                        this.errorDialogService.onErrorDialog(
                            [resources.errorMessages.PREFLIGHT_FAILURE_MSG],
                            false,
                            resources.errorMessages.PREFLIGHT_FAILURE_TITLE
                        );
                    }
                    return event;
                }
            }));
    }
    private findUrl(req: any): boolean {
        let urlFound = [];
        for (const url in this.blacklist) {
            const res = new RegExp(this.blacklist[url]).test(req.url);
            if (res) { urlFound.push(this.blacklist[url]); }
        }
        return urlFound.length === 0 ? false : true;
    }

    private findUrlAndErrorCode(req: HttpRequest<any>, error: HttpErrorResponse) {
        const findMatch = this.ignoreByUrlAndErrorCodeList.find(item =>
            item.code === error.status && new RegExp(item.url).test(req.url));
        return findMatch;
    }

    private reportRollbarError(error: any): boolean {
        return error && error.hasOwnProperty('reportError') ? error.reportError : true;
    }

    private validateError(req, errorReq = null) {
        let error = resources.errorMessages.DEFAULT;
        this.errorDialogService.onErrorDialog([error]);
    }

    private reportErrorGA(errorMsj: string): void {
        this.gaService.trackErrorExceptions(
            errorMsj, true
        );
    }
}
